import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface DataPoint {
  name: string;
  value: number;
  description: string;
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  data?: string | DataPoint[];
  yAxisLabel?: string;
  xAxisLabel?: string;
  teacherNote?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

interface EditableFieldProps {
  value: string | number;
  onChange: (value: string | number) => void;
  type?: "text" | "number";
  className?: string;
}

const EditableField: React.FC<EditableFieldProps> = ({
  value,
  onChange,
  type = "text",
  className = "",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value.toString());

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onChange(type === "number" ? Number(tempValue) : tempValue);
      setIsEditing(false);
    } else if (e.key === "Escape") {
      setTempValue(value.toString());
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        type={type}
        value={tempValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTempValue(e.target.value)
        }
        onBlur={() => {
          onChange(type === "number" ? Number(tempValue) : tempValue);
          setIsEditing(false);
        }}
        onKeyDown={handleKeyDown}
        className={`bg-white/20 rounded px-2 py-1 outline-none focus:ring-2 focus:ring-green-300 ${className}`}
        autoFocus
      />
    );
  }

  return (
    <span
      onClick={() => setIsEditing(true)}
      className="cursor-pointer hover:bg-white/10 px-2 py-1 rounded transition-colors"
    >
      {value.toString()}
    </span>
  );
};

const DataSlide = {
  id: "data-slide",
  name: "Data Visualization",
  fields: [
    { id: "title", label: "Title", type: "text" },
    { id: "subtitle", label: "Subtitle", type: "text" },
    { id: "data", label: "Data", type: "text" },
    { id: "yAxisLabel", label: "Y-Axis Label", type: "text" },
    { id: "xAxisLabel", label: "X-Axis Label", type: "text" },
    { id: "teacherNote", label: "Teacher Note", type: "text" },
    { id: "bgColor", label: "Background Color", type: "text" },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      yAxisLabel = "",
      xAxisLabel = "",
      teacherNote = "",
      bgColor = "linear-gradient(135deg, #166534 0%, #15803d 50%, #16a34a 100%)",
      data = "[]",
      onEdit = () => {},
    } = content;

    let parsedData: DataPoint[] = [];
    try {
      if (typeof data === "string") {
        parsedData = JSON.parse(data);
      } else if (Array.isArray(data)) {
        parsedData = data;
      }
    } catch (error) {
      console.warn("Error parsing data:", error);
    }

    const handleDataUpdate = (newData: DataPoint[]) => {
      if (onEdit) {
        onEdit({
          ...content,
          data: JSON.stringify(newData),
        });
      }
    };

    const updateDataPoint = (
      index: number,
      field: keyof DataPoint,
      value: string | number,
    ) => {
      const newData = [...parsedData];
      newData[index] = { ...newData[index], [field]: value };
      handleDataUpdate(newData);
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full transform translate-x-1/3 -translate-y-1/2" />
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-white/5 rounded-full" />
        </div>

        <div className="relative z-10 flex flex-col h-full p-4">
          <div className="text-center mb-4">
            <h1 className="text-3xl sm:text-4xl font-black mb-2">
              <EditableField
                value={title}
                onChange={(value) =>
                  onEdit({ ...content, title: value.toString() })
                }
              />
            </h1>
            <p className="text-lg sm:text-xl font-light text-green-100/90">
              <EditableField
                value={subtitle}
                onChange={(value) =>
                  onEdit({ ...content, subtitle: value.toString() })
                }
              />
            </p>
            <div className="w-32 h-1 mx-auto mt-2 bg-gradient-to-r from-green-300/50 to-transparent" />
          </div>

          <div className="flex-1 flex gap-4">
            <div className="w-2/3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={parsedData}
                  margin={{ top: 10, right: 30, left: 20, bottom: 40 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.1)"
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#fff" }}
                    label={{
                      value: xAxisLabel,
                      position: "bottom",
                      fill: "#fff",
                    }}
                  />
                  <YAxis
                    tick={{ fill: "#fff" }}
                    label={{
                      value: yAxisLabel,
                      angle: -90,
                      position: "insideLeft",
                      fill: "#fff",
                    }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload as DataPoint;
                        return (
                          <div className="bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow-lg border border-green-200">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-2xl">{data.icon}</span>
                              <span className="font-bold text-green-800">
                                {data.name}
                              </span>
                            </div>
                            <div className="mt-2 font-semibold text-green-900">
                              {data.value}%
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="url(#colorGradient)"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={80}
                  />
                  <defs>
                    <linearGradient
                      id="colorGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#4ade80" stopOpacity={1} />
                      <stop
                        offset="100%"
                        stopColor="#22c55e"
                        stopOpacity={0.8}
                      />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="w-1/3 flex flex-col gap-4">
              {parsedData.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-4"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{item.icon}</span>
                    <EditableField
                      value={item.name}
                      onChange={(value) =>
                        updateDataPoint(idx, "name", value.toString())
                      }
                      className="font-semibold"
                    />
                    <EditableField
                      value={item.value}
                      onChange={(value) =>
                        updateDataPoint(idx, "value", Number(value))
                      }
                      type="number"
                      className="ml-auto"
                    />
                  </div>
                  <EditableField
                    value={item.description}
                    onChange={(value) =>
                      updateDataPoint(idx, "description", value.toString())
                    }
                    className="text-sm text-white/80"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="relative mt-4">
            <div className="absolute -left-4 top-1/2 transform -translate-y-1/2 w-3 h-3 bg-yellow-400 rounded-full animate-ping" />
            <p className="text-sm text-center font-light italic text-green-100/90">
              <EditableField
                value={teacherNote}
                onChange={(value) =>
                  onEdit({ ...content, teacherNote: value.toString() })
                }
              />
            </p>
          </div>
        </div>
      </div>
    );
  },
};

export default DataSlide;
